import { useState } from "react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { OverviewSettingRow } from "@/components/agent/overview/OverviewSettingRow";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { useRole } from "@/hooks/auth/useRole";
import { Agent } from "@/types/agent/agent";
import { getGenieTypeMeta } from "@/types/genie-type";
import { GenieCategories } from "@/types/shared/categories";
import { LANGUAGES } from "@/utils/config/elevenlabs";

interface IdentityProfileCardProps {
  agent: Agent;
  brand?: string;
}

export const IdentityProfileCard = ({ agent, brand = "#4E9CFF" }: IdentityProfileCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(agent.name ?? "");
  const [description, setDescription] = useState(agent.description ?? "");
  const [agentBrand, setAgentBrand] = useState(agent.brand ?? "");
  const [category, setCategory] = useState(agent.category ?? "");
  const [type, setType] = useState(agent.type ?? "");
  const [isPublic, setIsPublic] = useState(agent.is_public ?? false);
  const [isMarketplace, setIsMarketplace] = useState(agent.is_marketplace_genie ?? false);
  const [isActive, setIsActive] = useState(agent.is_active ?? true);
  const [isSetup, setIsSetup] = useState(agent.is_setup ?? false);

  // A listener's type is what makes it a listener, so it is not editable here,
  // and its language (normally under Voice & Speech) is what it transcribes in.
  const isListener = agent.type === "listener";
  const agentConfig = agent.settings?.conversation_config?.agent;
  const [language, setLanguage] = useState(agentConfig?.language ?? "en");
  const currentLanguage = LANGUAGES.find((l) => l.code === (agentConfig?.language ?? "en"));

  const { isInternalAdmin } = useRole();
  const updateAgent = useUpdateAgent();

  const handleEdit = () => {
    setName(agent.name ?? "");
    setDescription(agent.description ?? "");
    setAgentBrand(agent.brand ?? "");
    setCategory(agent.category ?? "");
    setType(agent.type ?? "");
    setIsPublic(agent.is_public ?? false);
    setIsMarketplace(agent.is_marketplace_genie ?? false);
    setIsActive(agent.is_active ?? true);
    setIsSetup(agent.is_setup ?? false);
    setLanguage(agentConfig?.language ?? "en");
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSave = async () => {
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        name,
        description,
        brand: agentBrand,
        category,
        ...(isListener
          ? {
              settings: {
                ...agent.settings,
                conversation_config: {
                  ...agent.settings?.conversation_config,
                  agent: { ...agentConfig, language },
                },
              },
            }
          : { type: type || null }),
        is_public: isPublic,
        is_marketplace_genie: isMarketplace,
        ...(isInternalAdmin() && { is_active: isActive, is_setup: isSetup }),
      },
    });
    toast.success("Profile saved");
    setIsEditing(false);
  };

  const currentCategory =
    agent.category_data ??
    GenieCategories.find((c) => c.id === agent.category) ??
    GenieCategories.find((c) => c.id === "other")!;

  return (
    <OverviewSectionCard
      title="Identity & Purpose"
      subtitle="Basic information and profile settings"
      genieIcon="genie-lamp"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? handleCancel : undefined}
      isSaving={updateAgent.isPending}
    >
      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 h-32 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Describe what this genie does..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
            <input
              type="text"
              value={agentBrand}
              onChange={(e) => setAgentBrand(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select Category</option>
                {GenieCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            {isListener ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transcription Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm"
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  <option value="leads">Leads</option>
                  <option value="support">Support</option>
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            {[
              { label: "Public Genie", description: "Anyone with the link can access — not listed in marketplace", checked: isPublic, onChange: setIsPublic },
              { label: "Marketplace Genie", description: "Available in the Help Genie marketplace", checked: isMarketplace, onChange: setIsMarketplace },
              ...(isInternalAdmin() ? [
                { label: "Active", description: "Enable or disable this genie", checked: isActive, onChange: setIsActive },
                { label: "Setup Genie", description: "Creates, edits, or reviews customer genies", checked: isSetup, onChange: setIsSetup },
              ] : []),
            ].map(({ label, description, checked, onChange }) => (
              <div key={label} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                <div>
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <Switch
                  checked={checked}
                  onCheckedChange={onChange}
                  className={checked ? "data-[state=checked]:bg-transparent" : ""}
                  style={checked ? { backgroundColor: brand } : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow label="Name" value={<span className="font-semibold">{agent.name}</span>} />
          {currentCategory && (
            <OverviewSettingRow
              label="Category"
              value={
                <Badge
                  variant="outline"
                  style={{
                    borderColor: `${currentCategory.color}55`,
                    color: currentCategory.color,
                    backgroundColor: `${currentCategory.color}10`,
                  }}
                >
                  {currentCategory.label}
                </Badge>
              }
            />
          )}
          <OverviewSettingRow
            label="Type"
            value={
              <Badge
                variant="outline"
                style={{ borderColor: `${brand}55`, color: brand, backgroundColor: `${brand}10` }}
              >
                {getGenieTypeMeta(agent.type).label}
              </Badge>
            }
          />
          {isListener && (
            <OverviewSettingRow
              label="Transcription Language"
              value={
                <span className="font-medium">
                  {currentLanguage
                    ? `${currentLanguage.flag} ${currentLanguage.name}`
                    : (agentConfig?.language?.toUpperCase() ?? "EN")}
                </span>
              }
            />
          )}
          <OverviewSettingRow
            label="Status"
            value={
              <div className="flex items-center gap-2">
                <Badge variant={agent.is_active ? "default" : "secondary"}>
                  {agent.is_active ? "Active" : "Inactive"}
                </Badge>
                {!agent.is_active && agent.deactivated_manually && (
                  <span className="text-xs text-gray-500">Deactivated manually — won't be auto-reactivated by billing sync</span>
                )}
              </div>
            }
          />
          <OverviewSettingRow
            label="Visibility"
            value={
              <Badge variant={agent.is_public ? "default" : "secondary"}>
                {agent.is_public ? "Public" : "Private"}
              </Badge>
            }
          />
          <OverviewSettingRow
            label="Marketplace"
            value={
              <Badge variant={agent.is_marketplace_genie ? "default" : "secondary"}>
                {agent.is_marketplace_genie ? "Yes" : "No"}
              </Badge>
            }
          />
          {agent.brand && <OverviewSettingRow label="Brand" value={agent.brand} />}
          {agent.description && <OverviewSettingRow label="Description" value={agent.description} fullWidth />}
        </div>
      )}
    </OverviewSectionCard>
  );
};
